import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

// LIST
export function useListAddresses() {
  return useQuery({
    queryKey: ['addresses:list'],
    queryFn: async () => {
      const { data } = await api.get('/addresses');
      return data.addresses || [];
    },
  });
}

/**
 * The fields a delivery address is created or updated with.
 *
 * Named rather than left implicit: an untyped `mutationFn` parameter makes
 * TanStack infer `TVariables` as `void`, so callers were rejected for passing
 * the very payload this sends.
 */
export interface AddressPayload {
  label?: string;
  fullName?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isPrimary?: boolean;
  isHeadquarters?: boolean;
  operatingHours?: import('./operatingHours').OperatingHours | null;
}

/** An update names the address it edits; every other field is a partial edit. */
export type UpdateAddressPayload = AddressPayload & { id: string };

/**
 * One saved address as the list cache holds it. Only the fields the optimistic
 * update actually touches are named — the server remains the source of truth
 * for the rest, which is why the mutation returns `data.address`.
 */
export type AddressRecord = AddressPayload & { id: string };

// CREATE
export function useCreateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddressPayload) => {
      const { data } = await api.post('/addresses', payload);
      return data.address;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addresses:list'] });
    },
  });
}

// UPDATE
export function useUpdateAddress() {
  const qc = useQueryClient();

  return useMutation({
    // actual network call
    mutationFn: async (payload: UpdateAddressPayload) => {
      const { id, ...rest } = payload;
      const { data } = await api.put(`/addresses/${id}`, rest);
      return data.address; // <- server is source of truth
    },

    // optimistic update
    onMutate: async (payload: UpdateAddressPayload) => {
      await qc.cancelQueries({ queryKey: ['addresses:list'] });

      const previous = qc.getQueryData(['addresses:list']); // [{...}, ...]

      // Optimistically update cache
      qc.setQueryData(['addresses:list'], (old: AddressRecord[] | undefined) => {
        if (!Array.isArray(old)) return old;

        // Apply field changes to the edited address
        let next = old.map((a) => (a.id === payload.id ? { ...a, ...payload } : a));

        // If this update makes the address primary, clear others
        if (payload.isPrimary === true) {
          next = next.map((a) =>
            a.id === payload.id ? { ...a, isPrimary: true } : { ...a, isPrimary: false }
          );
        }

        return next;
      });

      // pass snapshot for rollback
      return { previous };
    },

    // rollback on error
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['addresses:list'], ctx.previous);
      }
    },

    // reconcile with server / refresh
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['addresses:list'] });
    },
  });
}

// DELETE
export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.delete(`/addresses/${id}`);
      return data.address;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['addresses:list'] });
    },
  });
}
